/**
 * ParentConsent — one-time consent screen shown on the parent's first
 * visit to the dashboard (#PL-WEB-PARENT).
 *
 * Acknowledgement writes `parent_accounts.consent_acknowledged_at` via
 * a direct upsert from the browser (the RLS policy on parent_accounts
 * permits the user to update their own consent timestamp — see
 * migration #PL-MSG-MIG). The dashboard's redirect logic skips this
 * screen on subsequent visits: `consent_acknowledged_at IS NOT NULL` →
 * skip.
 *
 * Per the project rules (effectiveslp-ios rule #5 — plain language),
 * the copy is intentionally non-clinical. The disclosure is pinned to
 * the form, not a footer, so the parent can't acknowledge without
 * seeing it.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { useParentAuth } from '../../lib/auth/useParentAuth';
import { BrandMonogram } from '../../lib/effectiveslp/BrandMonogram';

interface Props {
  onAcknowledged: () => void;
  onSignOut?: () => void;
}

export function ParentConsent({ onAcknowledged, onSignOut }: Props): ReactElement {
  const { user } = useParentAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAcknowledge(): Promise<void> {
    if (!user) {
      setError('Sign in expired. Please request a new magic link.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // First, ensure the parent_accounts row exists (the dashboard
      // router may not have called api-create-parent-account yet if
      // the user landed here directly from the magic link).
      const { error: createErr } = await supabase.functions.invoke(
        'api-create-parent-account',
        { body: {} },
      );
      if (createErr) {
        throw new Error(createErr.message);
      }

      // Then, write the consent timestamp. The service-role-mediated
      // upsert in api-create-parent-account guarantees the row
      // exists; the parent can now flip consent_acknowledged_at via
      // the RLS-permitted UPDATE on their own row.
      const { error: ackErr } = await supabase
        .from('parent_accounts')
        .update({ consent_acknowledged_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (ackErr) {
        throw new Error(ackErr.message);
      }
      onAcknowledged();
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Could not save your acknowledgement. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  }

  return (
    <main className="screen auth parent">
      <div className="auth-hero">
        <BrandMonogram size={64} />
        <header className="screen-header">
          <h1>Before you look at your kid&rsquo;s practice</h1>
          <p className="tagline">
            A few quick things to know before we show you the dashboard.
          </p>
        </header>
      </div>

      <section className="card stack-md" aria-label="Disclosure">
        <p>
          <strong>EffectiveSLP</strong> is for sharing practice updates
          with your SLP.
        </p>
        <p>
          <strong>Messages here are not for emergencies.</strong> If your
          kid needs urgent help, call your SLP or 911 directly.
        </p>
        <p>
          By continuing, you agree that messages may be reviewed by your
          SLP and are kept for 90 days after your kid&rsquo;s practice
          ends.
        </p>
      </section>

      <div className="auth-form">
        <button
          type="button"
          className="snap-button"
          onClick={() => void handleAcknowledge()}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'I understand — continue'}
        </button>

        {error && <p className="error">{error}</p>}

        {onSignOut && (
          <button
            type="button"
            className="link-button"
            onClick={() => void onSignOut()}
          >
            Sign out
          </button>
        )}
      </div>
    </main>
  );
}
